/**
 * chat.ts — Minimal, functional chat UI logic for the Joke Chat (Phase 2)
 *
 * Responsibilities:
 * - Maintain an in-memory chat history of UiMessageHistoryItem messages.
 * - Wire DOM controls (messages list, input, send button, status) with a provided client via DI.
 * - Safely render messages using textContent (never innerHTML) to avoid XSS.
 * - Provide structured logging for init, user actions, network lifecycle, and rendering updates.
 *
 * Exports:
 * - setupChatUI({ requestAgentResponse }) — Initializes the chat UI on the existing DOM elements,
 *   registers handlers, and returns a cleanup function for viewport-sync listeners.
 * - initChatWithRealClient(endpoint, welcomeConfig) — Helper that wires the UI to the real client using the given endpoint.
 */

import { logger, newCid } from "./logger.js";
import { requestAgentResponseNew as realRequestAgentResponse } from "./agentClient.js";
import type {
    UiMessageHistoryItem,
    JonasAgentResponseBody,
    RenderableContentBlock,
    AcceptableNextBestAction,
} from "./types.js";

// Import and register components
import "./components/IconComponent.js";
import "./components/SimpleTextBlockComponent.js";
import "./components/JokeBlockComponent.js";
import "./components/LoadingBlockComponent.js";
import "./components/ErrorBlockComponent.js";
import "./components/NotFoundBlockComponent.js";
import "./components/ProjectBlockComponent.js";
import "./components/TestimonialBlockComponent.js";
import "./components/UiMessageBlockComponent.js";
import "./components/NextBestActionBlockComponent.js";
import "./components/AcceptedNextBestActionBlockComponent.js";
import "./components/ParticipantMessageSubListComponent.js";
import "./components/MessageHistoryComponent.js";
import type { MessageHistoryComponent } from "./components/MessageHistoryComponent.js";
import { installViewportHeightSync } from "./viewportHeightSync.js";

// DOM element IDs used by index.html
const MESSAGES_LIST_SELECTOR = "jt-message-history"; // <jt-message-history>
const COMPOSER_INPUT_SELECTOR = ".composer__input"; // <textarea>
const COMPOSER_SEND_BUTTON_SELECTOR = ".composer__send"; // <button>
const COMPOSER_HIDDEN_STATUS_SELECTOR = ".composer__hidden-status"; // <p aria-live="polite">

// Assistant messages used by the UI
const FOLLOWUP_PROMPT = "What else can I do for you?";

export type ConfigurableWelcomeNba = {
    id: string;
    text: string;
};

export type ChatWelcomeConfig = {
    welcomeMessages: string[];
    defaultWelcomeNbas: ConfigurableWelcomeNba[];
    welcomeMessageDelayMs: number;
};

/**
 * Dependencies for the chat UI — fully injectable for testing.
 */
export type ChatDeps = ChatWelcomeConfig & {
    /**
     * Function that sends the full chat history to the backend and returns a structured response.
     * The function MUST NOT mutate the history array.
     */
    requestAgentResponse: (history: UiMessageHistoryItem[], signal?: AbortSignal) => Promise<JonasAgentResponseBody>;
};

/**
 * Append messages to the messages list in the DOM, using components.
 * 
 * @param list The message history component to append to.
 * @param role The role of the message sender ("user" or "assistant").
 * @param contentBlocks The content blocks to render.
 */
function appendMessages(
    list: MessageHistoryComponent,
    role: "user" | "assistant",
    contentBlocks: RenderableContentBlock[]
): void {
    // Pass the content blocks directly to the component for rendering
    list.addBlocks(role, contentBlocks);

    // Log rendering for observability
    logger.debug("appendMessages: rendered components", { role, count: contentBlocks.length });
}

/**
 * Enable/disable controls and reflect loading state in the aria-live status region.
 */
function setLoading(loading: boolean, btn: HTMLButtonElement, status: HTMLElement): void {
    // Input and button remain enabled during loading to allow abortion/typing.
    if (loading) {
        btn.classList.add("stop-loading");
        btn.setAttribute("aria-label", "Stop generating");
    } else {
        btn.classList.remove("stop-loading");
        btn.setAttribute("aria-label", "Send message");
    }
    status.textContent = loading ? "Loading..." : "";
    logger.debug("setLoading: toggled", { loading });
}


/**
 * Align the newest turn wrapper with the top edge of the `.messages` viewport.
 *
 * This is the only force-scroll used by the chat UI. It is invoked only when a
 * new user turn starts so assistant updates can render without pulling the
 * viewport away from a manual user scroll position.
 */
function alignLatestTurnInMessagesViewport(
    list: MessageHistoryComponent,
    scrollContainer: HTMLElement,
    topInset: number
): void {
    list.scrollLatestTurnToTop(scrollContainer, topInset);
}

/**
 * Safely get required DOM elements and throw explicit errors if any are missing.
 */
function getDom(): { list: MessageHistoryComponent; input: HTMLTextAreaElement; btn: HTMLButtonElement; status: HTMLElement;} {
    const listEl = document.querySelector(MESSAGES_LIST_SELECTOR);
    const inputEl = document.querySelector(COMPOSER_INPUT_SELECTOR);
    const btnEl = document.querySelector(COMPOSER_SEND_BUTTON_SELECTOR);
    const statusEl = document.querySelector(COMPOSER_HIDDEN_STATUS_SELECTOR);

    if (!(listEl instanceof HTMLElement) || listEl.tagName.toLowerCase() !== "jt-message-history" || !(inputEl instanceof HTMLTextAreaElement) || !(btnEl instanceof HTMLButtonElement) || !(statusEl instanceof HTMLElement)) {
        throw new Error("Chat UI elements not found");
    }

    logger.debug("getDom: acquired elements", {
        list: Boolean(listEl),
        input: Boolean(inputEl),
        btn: Boolean(btnEl),
        status: Boolean(statusEl),
    });

    return { list: listEl as MessageHistoryComponent, input: inputEl, btn: btnEl, status: statusEl};
}

/**
 * Resize the textarea to fit content up to the maximum height defined in CSS.
 */
function resizeInputToContent(input: HTMLTextAreaElement): void {
    if (typeof window === 'undefined' || !window.getComputedStyle) {
        return;
    }
    const styles = window.getComputedStyle(input);
    const borderTop = parseFloat(styles.borderTopWidth || "0");
    const borderBottom = parseFloat(styles.borderBottomWidth || "0");

    // Reset height to auto to get the correct scrollHeight for the content
    input.style.height = "auto";
    
    // Set height to scrollHeight + borders (for border-box)
    // The CSS max-height will automatically cap this value.
    const nextHeight = input.scrollHeight + borderTop + borderBottom;
    input.style.height = `${nextHeight}px`;

    // Toggle scrollbar if content exceeds the actual height (which is capped by max-height)
    // We use a small threshold to avoid sub-pixel flickering issues.
    const isOverflowing = input.scrollHeight > input.clientHeight + 1;
    input.style.overflowY = isOverflowing ? "auto" : "hidden";
}

/**
 * Initialize the chat UI with provided dependencies.
 *
 * Behavior:
 * - Renders an initial assistant greeting (UiMessageContentBlock).
 * - On send: appends user message (SimpleTextContentBlock), clears input immediately, disables controls, sets loading, calls deps.requestAgentResponse with full history.
 * - On success: replaces the loading placeholder with the assistant response (FullContentBlock) and adds a follow-up assistant prompt (UiMessageContentBlock); re-enables controls and focuses input.
 * - On error: replaces the loading placeholder with the standardized assistant error message (ErrorContentBlock); re-enables controls and focuses input.
 *
 * @returns A cleanup function that disconnects viewport-height synchronization observers/listeners.
 */
export function setupChatUI(deps: ChatDeps): () => void {
    const cid = newCid("chat");
    const history: UiMessageHistoryItem[] = [];
    let currentController: AbortController | null = null;
    let isGeneratingWelcome = false;
    let welcomeGenerationToken = 0;
    let welcomeDelayTimer: ReturnType<typeof setTimeout> | null = null;
    let welcomeDelayResolve: (() => void) | null = null;

    logger.info("Chat UI setup: start", { cid });

    if (!deps || typeof deps.requestAgentResponse !== "function") {
        throw new Error("setupChatUI requires a requestAgentResponse dependency");
    }

    const { list, input, btn, status } = getDom();
    const resizeInput = (): void => resizeInputToContent(input);

    // --- Viewport height propagation ---
    // Delegate to the extracted helper which measures the visible message-list
    // viewport from body.offsetHeight minus the current header/composer overlays,
    // one --chat-participants-gap, and the shared --messages-edge-gap token.
    // It observes body, header, and composer with ResizeObserver (or falls back
    // to window.resize) so that composer growth/shrink and window resizes
    // trigger a recalculation.
    // See viewportHeightSync.ts for the full rationale on the body-based approach.
    const headerEl = document.querySelector<HTMLElement>(".header");
    const composerEl = document.querySelector<HTMLElement>(".composer");
    const messagesEl = document.querySelector<HTMLElement>(".messages");

    if (!headerEl || !composerEl || !messagesEl) {
        throw new Error("Chat viewport sync: required layout elements (.header, .composer, .messages) not found");
    }

    const disposeViewportSync = installViewportHeightSync({
        body: document.body,
        header: headerEl,
        composer: composerEl,
        messages: messagesEl,
        list,
    });
    const welcomeMessages = deps.welcomeMessages;
    const defaultWelcomeNbas: AcceptableNextBestAction[] = deps.defaultWelcomeNbas.map((nba) => ({
        id: nba.id,
        text: nba.text,
        previouslyShownContentBlockId: { type: "NOT_FOUND" },
    }));
    const welcomeMessageDelayMs = deps.welcomeMessageDelayMs;

    // --- Helpers ---

    /**
     * Centralized helper to update both history and UI in sync.
     */
    const pushToHistoryAndUi = (item: UiMessageHistoryItem): void => {
        history.push(item);

        const blocks: RenderableContentBlock[] = 'contentBlock' in item
            ? [item.contentBlock]
            : [...item.contentBlocks];

        // Automatically append NBAs as blocks for assistant messages
        if (item.role === 'ASSISTANT' && item.nbas) {
            blocks.push(...item.nbas.map(nba => ({ type: 'NEXT_BEST_ACTION' as const, nba })));
        }

        appendMessages(list, item.role.toLowerCase() as "user" | "assistant", blocks);
    };

    const abortCurrentRequest = (): void => {
        if (currentController) {
            logger.debug("Chat request: aborting", { cid });
            currentController.abort();
            list.setLoading("assistant", false);
            setLoading(false, btn, status);
            currentController = null;
        }
    };

    const waitForWelcomeDelay = async (): Promise<void> => {
        await new Promise<void>((resolve) => {
            welcomeDelayResolve = resolve;
            welcomeDelayTimer = setTimeout(() => {
                welcomeDelayTimer = null;
                welcomeDelayResolve = null;
                resolve();
            }, welcomeMessageDelayMs);
        });
    };

    const stopWelcomeGeneration = (): void => {
        if (!isGeneratingWelcome) {
            return;
        }

        welcomeGenerationToken += 1;
        isGeneratingWelcome = false;

        if (welcomeDelayTimer !== null) {
            clearTimeout(welcomeDelayTimer);
            welcomeDelayTimer = null;
        }
        if (welcomeDelayResolve) {
            const resolve = welcomeDelayResolve;
            welcomeDelayResolve = null;
            resolve();
        }

        list.setLoading("assistant", false);
        setLoading(false, btn, status);
        logger.info("Welcome sequence: stopped", { cid, shownMessages: history.length });
    };

    const runWelcomeSequence = async (): Promise<void> => {
        const token = ++welcomeGenerationToken;
        isGeneratingWelcome = true;
        setLoading(true, btn, status);
        logger.info("Welcome sequence: start", {
            cid,
            messageCount: welcomeMessages.length,
            defaultNbaCount: defaultWelcomeNbas.length,
            delayMs: welcomeMessageDelayMs,
        });

        try {
            for (let i = 0; i < welcomeMessages.length; i += 1) {
                if (welcomeGenerationToken !== token) {
                    return;
                }

                if (i > 0) {
                    list.setLoading("assistant", true);
                    await waitForWelcomeDelay();
                    if (welcomeGenerationToken !== token) {
                        return;
                    }
                    list.setLoading("assistant", false);
                }

                const isLastMessage = i === welcomeMessages.length - 1;
                const text = welcomeMessages[i];
                if (isLastMessage && defaultWelcomeNbas.length > 0) {
                    pushToHistoryAndUi({
                        role: "ASSISTANT",
                        contentBlock: { type: "UI_MESSAGE", text },
                        nbas: defaultWelcomeNbas,
                    });
                } else {
                    pushToHistoryAndUi({
                        role: "ASSISTANT",
                        contentBlock: { type: "UI_MESSAGE", text },
                    });
                }
            }
        } finally {
            if (welcomeGenerationToken === token) {
                isGeneratingWelcome = false;
                list.setLoading("assistant", false);
                setLoading(false, btn, status);
                logger.info("Welcome sequence: complete", { cid, shownMessages: welcomeMessages.length });
            }
        }
    };

    /**
     * Handles the mapping of a successful API response to history and UI.
     */
    const handleResponse = (response: JonasAgentResponseBody, startedAt: number): void => {
        const res = response.content;
        const durationMs = Date.now() - startedAt;
        const nbas = response.nbas || [];

        if (res.type === 'ERROR') {
            pushToHistoryAndUi({ role: 'ASSISTANT', contentBlock: res });
            logger.warn("Chat request: backend error", { cid, durationMs, error: res.error });
        } else if (res.type === 'NOT_FOUND') {
            pushToHistoryAndUi({ role: 'ASSISTANT', contentBlock: res, nbas });
            logger.info("Chat request: backend not found", { cid, durationMs });
        } else {
            pushToHistoryAndUi({ role: 'ASSISTANT', contentBlocks: res.blocks, nbas });

            // Add follow-up prompt if no NBAs are available
            if (nbas.length === 0) {
                pushToHistoryAndUi({ role: 'ASSISTANT', contentBlock: { type: 'UI_MESSAGE', text: FOLLOWUP_PROMPT } });
            }
            logger.info("Chat request: success", { cid, durationMs, count: res.blocks.length, nbaCount: nbas.length });
        }
    };

    /**
     * Core async flow for fetching agent responses.
     */
    const performRequest = async (shouldRefocusInput: boolean): Promise<void> => {
        const startedAt = Date.now();
        const controller = new AbortController();
        currentController = controller;

        list.setLoading("assistant", true);
        setLoading(true, btn, status);

        let responseType = "Exception";
        logger.debug("Chat request: start", { cid, historyCount: history.length, startedAt });

        try {
            const response = await deps.requestAgentResponse(history, controller.signal);
            if (currentController !== controller) {
                return;
            }

            responseType = response.content.type;
            list.setLoading("assistant", false);
            handleResponse(response, startedAt);
        } catch (err) {
            if (currentController !== controller) {
                return;
            }

            list.setLoading("assistant", false);
            if (controller.signal.aborted) {
                responseType = "Aborted";
                logger.info("Chat request: aborted", { cid, durationMs: Date.now() - startedAt });
            } else {
                logger.error("Chat request: exception", { cid, error: String(err), durationMs: Date.now() - startedAt });
                pushToHistoryAndUi({ role: 'ASSISTANT', contentBlock: { type: 'ERROR', error: 'INTERNAL_ERROR' } });
            }
        } finally {
            if (currentController === controller) {
                setLoading(false, btn, status);
                if (shouldRefocusInput) {
                    input.focus();
                }
                resizeInput();
                currentController = null;
            }
            logger.debug("Chat request: end", { cid, type: responseType, durationMs: Date.now() - startedAt, historyCount: history.length });
        }
    };

    // --- Action Handlers ---

    const handleSend = async (trigger: 'click' | 'enter'): Promise<void> => {
        if (isGeneratingWelcome) {
            if (trigger === "click") {
                stopWelcomeGeneration();
            }
            return;
        }

        if (currentController) {
            if (trigger === 'click') {
                abortCurrentRequest();
            }
            return;
        }

        const text = input.value.trim();
        if (!text) {
            return;
        }

        input.value = "";
        resizeInput();
        pushToHistoryAndUi({ role: 'USER', contentBlock: { type: 'SIMPLE_TEXT', text } });
        alignLatestTurnInMessagesViewport(list, messagesEl, headerEl.offsetHeight);
        await performRequest(true);
    };

    const handleAcceptNba = async (nba: AcceptableNextBestAction): Promise<void> => {
        abortCurrentRequest();
        input.blur();
        logger.info("Chat NBA: accepted", { cid, nbaId: nba.id, text: nba.text });
        pushToHistoryAndUi({ role: 'USER', contentBlock: { type: 'ACCEPTED_NEXT_BEST_ACTION', nba } });
        alignLatestTurnInMessagesViewport(list, messagesEl, headerEl.offsetHeight);
        await performRequest(false);
    };

    // --- Initialization & Event Binding ---

    resizeInput();
    input.addEventListener("input", resizeInput);

    // Initial welcome sequence (messages + optional default NBAs)
    void runWelcomeSequence();

    // Events
    btn.addEventListener("click", () => { void handleSend('click'); });
    btn.addEventListener("mousedown", (ev) => ev.preventDefault());
    input.addEventListener("keydown", (ev) => {
        if (ev.key === "Enter") {
            ev.preventDefault();
            void handleSend('enter');
        }
    });
    list.addEventListener("accept-nba", (ev) => {
        void handleAcceptNba((ev as CustomEvent<AcceptableNextBestAction>).detail);
    });

    // Return a teardown function that cleans up viewport-sync observers/listeners.
    // In the current single-mount app this is intentionally not wired to any
    // lifecycle hook, but it is exposed so callers (and tests) can clean up
    // when needed (e.g. hot-reload or remount scenarios).
    return disposeViewportSync;
}

/**
 * Initialize the chat UI using the real request client and a provided endpoint.
 * This keeps main.ts small and test-friendly.
 */
export function initChatWithRealClient(endpoint: string, welcomeConfig: ChatWelcomeConfig): void {
    const cid = newCid("chat");
    logger.info("initChatWithRealClient: wiring real client", {
        cid,
        endpoint,
        welcomeMessageCount: welcomeConfig.welcomeMessages.length,
        defaultWelcomeNbaCount: welcomeConfig.defaultWelcomeNbas.length,
        welcomeMessageDelayMs: welcomeConfig.welcomeMessageDelayMs,
    });
    setupChatUI({
        requestAgentResponse: async (history, signal) => {
            // Delegate to the real client with explicit endpoint. Any fetch failures bubble up to UI handler.
            return await realRequestAgentResponse(history, (url, init) => {
                return fetch(url, { ...init, signal: signal ?? null });
            }, endpoint || "/");
        },
        ...welcomeConfig,
    });
}
